import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ImpersonationService } from '@mysrc/app/modules/fwcore/services/impersonation.service';
import { OktaWrapperService } from '@mysrc/services/framework/okta-wrapper.service';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import urlJoin from 'url-join';
import { fwHttpClientUtils } from '../models';
import { BaseHttpService } from './base-http.service';

/**
 * Extends BaseHttpService with typed GET/POST/PUT/DELETE convenience methods.
 * Token access and impersonation are fully inherited from BaseHttpService.
 * All application services should extend this rather than BaseHttpService directly.
 */
@Injectable({ providedIn: 'root' })
export class BaseHttpReqRespService extends BaseHttpService {

  /** Enable per-request timing logs to console. */
  public enableConsoleLogTiming: boolean = true;

  /**
   * @param http - HttpClient — passed up to BaseHttpService
   * @param config - Passed up to BaseHttpService
   * @param impersonation - Passed up to BaseHttpService
   * @param oktaWrapper - Passed up to BaseHttpService
   */
  constructor(
    http: HttpClient,
    @Inject('CONFIG_SETTINGS') config: any,
    impersonation: ImpersonationService,   
    oktaWrapper: OktaWrapperService        
  ) {
    super(http, config, impersonation, oktaWrapper);
  }

  // -------------------- GET --------------------

  /** GET with typed request params and typed response. */
  public getData$<Req, Resp>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Resp> {

    const token = this.getToken();
    const startTime = this.startTime(logTime);

    if (token) {
      const url = urlJoin(this.baseUrl, endpoint);
      return this.http.get<Resp>(url, {
        headers: this.getHttpBearerHeaders(token),
        params: fwHttpClientUtils.toHttpParams(req)
      }).pipe(
        tap(() => this.logTiming(startTime, 'getData$', endpoint))
      );
    }

    return of({} as Resp);
  }

  /** GET returning a Blob with typed request params. */
  public getBlobData$<Req>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Blob> {

    const token = this.getToken();
    const startTime = this.startTime(logTime);

    if (token) {
      const url = urlJoin(this.baseUrl, endpoint);
      return this.http.get<Blob>(url, {
        headers: this.getHttpBearerHeaders(token),
        responseType: 'blob' as 'json',
        params: fwHttpClientUtils.toHttpParams(req)
      }).pipe(
        tap(() => this.logTiming(startTime, 'getBlobData$', endpoint))
      );
    }

    return of(new Blob());
  }

  /** POST returning a Blob with typed request params. */
  public postBlobData$<Req>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Blob> {

    const token = this.getToken();
    const startTime = this.startTime(logTime);

    if (token) {
      const url = urlJoin(this.baseUrl, endpoint);

      return this.http.post<Blob>(
        url,
        req, // request body
        {
          headers: this.getHttpBearerHeaders(token),
          responseType: 'blob' as 'json'
        }
      ).pipe(
        tap(() => this.logTiming(startTime, 'postBlobData$', endpoint))
      );
    }

    return of(new Blob());
  }

  /** GET returning a Blob with no request params. */
  public getBlobDataWithoutRequest$(endpoint: string, logTime: boolean | null = null): Observable<Blob> {

    const token = this.getToken();
    const startTime = this.startTime(logTime);

    if (token) {
      const url = urlJoin(this.baseUrl, endpoint);
      return this.http.get(url, {
        headers: this.getHttpBearerHeaders(token),
        responseType: 'blob'
      }).pipe(
        tap(() => this.logTiming(startTime, 'getBlobDataWithoutRequest$', endpoint))
      );
    }

    return of(new Blob());
  }

  // -------------------- POST --------------------

  /** POST with typed request body and typed response. */
  public setData$<Req, Resp>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Resp> {

    const token = this.getToken();
    const startTime = this.startTime(logTime);

    if (token) {
      const url = urlJoin(this.baseUrl, endpoint);
      return this.http.post<Resp>(url, req, {
        headers: this.getHttpBearerHeaders(token)
      }).pipe(
        tap(() => this.logTiming(startTime, 'setData$', endpoint))
      );
    }

    return of({} as Resp);
  }

  // -------------------- PUT --------------------

  /** PUT with typed request body and typed response. */
  public putData$<Req, Resp>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Resp> {

    const token = this.getToken();
    const startTime = this.startTime(logTime);

    if (token) {
      const url = urlJoin(this.baseUrl, endpoint);
      return this.http.put<Resp>(url, req, {
        headers: this.getHttpBearerHeaders(token)
      }).pipe(
        tap(() => this.logTiming(startTime, 'putData$', endpoint))
      );
    }

    return of({} as Resp);
  }

  // -------------------- DELETE --------------------

  /** DELETE with typed request params and typed response. */
  public deleteData$<Req, Resp>(req: Req, endpoint: string, logTime: boolean | null = null): Observable<Resp> {

    const token = this.getToken();
    const startTime = this.startTime(logTime);

    if (token) {
      const url = urlJoin(this.baseUrl, endpoint);
      return this.http.delete<Resp>(url, {
        headers: this.getHttpBearerHeaders(token),
        params: fwHttpClientUtils.toHttpParams(req)
      }).pipe(
        tap(() => this.logTiming(startTime, 'deleteData$', endpoint))
      );
    }

    return of({} as Resp);
  }

  // -------------------- Timing helpers --------------------

  /** Returns current timestamp if timing is enabled, otherwise null. */
  private startTime(logTime: boolean | null): number | null {
    return (logTime ?? this.enableConsoleLogTiming) ? Date.now() : null;
  }

  /** Logs request duration to console if timing was enabled. */
  private logTiming(startTime: number | null, method: string, endpoint: string): void {
    if (startTime !== null) {
      console.log(`${method} to ${endpoint} took ${Date.now() - startTime}ms`);
    }
  }
}
